import type { ComponentFieldSpec } from "./constants/component-field-spec";

/**
 * Regenerates the `id` of every nested object-array item within `node` (recursively — e.g. a
 * footer's `columns[].links[]`), mutating in place, and remaps any `primitiveArray`
 * (`addressing: "value"`) field that references one of those arrays (e.g. a team member's
 * `labelIds` referencing `team.labels`) so it keeps pointing at the right item under its new id
 * instead of a now-stale one. Used by `duplicateComponent` (`_stores/builder-store.ts`) so a
 * cloned section's nested items get fresh ids without breaking cross-references to sibling
 * arrays. Derived from `COMPONENT_FIELD_SPEC`, so a new nested array field — or a new
 * `referencesArrayField` declaration — is covered automatically instead of needing a second
 * hand-maintained list that can drift out of sync.
 * @param node - The object being mutated in place (a component, or a nested item).
 * @param fieldsSpec - The spec map for `node`'s own fields.
 * @param visibleRemaps - Old-id → new-id maps for object-array fields already regenerated at this
 *   level or an ancestor's, keyed by field name — lets a field nested inside one array (e.g.
 *   `members[].labelIds`) resolve a reference to a sibling array at the parent level (`labels`).
 */
export function regenerateNestedIds(
  node: Record<string, unknown>,
  fieldsSpec: ComponentFieldSpec,
  visibleRemaps: Record<string, Map<string, string>> = {}
): void {
  const remapsAtThisLevel: Record<string, Map<string, string>> = { ...visibleRemaps };

  // Pass 1 — regenerate every direct object-array's item ids, recording each field's remap.
  for (const [key, spec] of Object.entries(fieldsSpec)) {
    if (spec.kind !== "objectArray") continue;
    const arr = node[key];
    if (!Array.isArray(arr)) continue;
    const remap = new Map<string, string>();
    for (const item of arr as Record<string, unknown>[]) {
      const oldId = item.id;
      const newId = crypto.randomUUID();
      item.id = newId;
      if (typeof oldId === "string") remap.set(oldId, newId);
    }
    remapsAtThisLevel[key] = remap;
  }

  // Pass 2 — remap any value-addressed list at THIS level that references one of the arrays just
  // regenerated (here or at an ancestor level, via `visibleRemaps`).
  for (const [key, spec] of Object.entries(fieldsSpec)) {
    if (spec.kind !== "primitiveArray" || spec.addressing !== "value" || !spec.referencesArrayField) continue;
    const remap = remapsAtThisLevel[spec.referencesArrayField];
    const arr = node[key];
    if (!remap || !Array.isArray(arr)) continue;
    node[key] = (arr as string[]).map((value) => remap.get(value) ?? value);
  }

  // Pass 3 — recurse into each object-array's items, carrying this level's remaps down so a
  // grandchild's value-addressed list can still resolve a reference regenerated here.
  for (const [key, spec] of Object.entries(fieldsSpec)) {
    if (spec.kind !== "objectArray") continue;
    const arr = node[key];
    if (!Array.isArray(arr)) continue;
    for (const item of arr as Record<string, unknown>[]) {
      regenerateNestedIds(item, spec.fields, remapsAtThisLevel);
    }
  }
}
